import React, { useMemo, useEffect } from "react";
import { TextField, Grid, Box, MenuItem } from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createDepartment, updateDepartment } from "../../../services/departmentService";
import BasicModal from "../../../components/BasicModal";
import FormModalActions from "../../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
  SUCCESS_STEP_MODAL_SX,
} from "../../../utils/formModalStyles";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import {
  filterDepartmentTypeOptionsForPackage,
  resolveDepartmentCareScope,
} from "../../../utils/subscriptionScopeUtils";

const DEPARTMENT_MODAL_SX = {
  ...SUCCESS_STEP_MODAL_SX,
  width: "min(620px, 96vw)",
  maxWidth: "620px",
};

const DEPARTMENT_TYPE_OPTIONS = [
  { value: "OPD", label: "OPD" },
  { value: "BOTH", label: "OPD + IPD" },
  { value: "IPD", label: "IPD / Premium" },
];

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Department name is required"),
  code: Yup.string().trim().nullable(),
  description: Yup.string().trim().nullable(),
  departmentType: Yup.string().oneOf(["OPD", "IPD", "BOTH"]).nullable(),
});

const DepartmentModal = ({ open, onClose, departmentInfo }) => {
  const isEditMode = Boolean(departmentInfo?.id);
  const { isPremiumPackage } = useSubscriptionCapabilities();

  const initialType =
    departmentInfo?.departmentType ||
    departmentInfo?.department_type ||
    departmentInfo?.type ||
    (departmentInfo?.name
      ? resolveDepartmentCareScope(departmentInfo)
      : "OPD");

  const formik = useFormik({
    initialValues: {
      name: departmentInfo?.name || "",
      code: departmentInfo?.code || "",
      description: departmentInfo?.description || "",
      departmentType: initialType || "OPD",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          name: values.name.trim(),
          code: values.code?.trim() || null,
          description: values.description?.trim() || null,
          departmentType: values.departmentType || "OPD",
        };

        if (isEditMode) {
          await updateDepartment(departmentInfo.id, payload);
          alert("Department updated successfully");
        } else {
          await createDepartment(payload);
          alert("Department created successfully");
        }
        resetForm();
        onClose(true);
      } catch (error) {
        console.error("Failed to save department:", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save department.";
        alert(message);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const visibleTypeOptions = useMemo(
    () =>
      filterDepartmentTypeOptionsForPackage(DEPARTMENT_TYPE_OPTIONS, {
        isPremium: isPremiumPackage,
        keepValue: formik.values.departmentType,
      }),
    [isPremiumPackage, formik.values.departmentType]
  );

  // Suggest care-scope from specialty name when creating (or when name changes in create mode)
  useEffect(() => {
    if (!open || isEditMode) return;
    const name = String(formik.values.name || "").trim();
    if (!name) return;
    const suggested = resolveDepartmentCareScope({ name });
    const allowed = visibleTypeOptions.some((o) => o.value === suggested);
    if (allowed && formik.values.departmentType !== suggested) {
      formik.setFieldValue("departmentType", suggested);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name, open, isEditMode, isPremiumPackage]);

  return (
    <BasicModal
      open={open}
      onClose={() => {
        formik.resetForm();
        onClose(false);
      }}
      title={isEditMode ? "Edit Department" : "Add Department"}
      sx={DEPARTMENT_MODAL_SX}
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX, px: 0.5, pb: 0.5 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Department Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                id="code"
                name="code"
                label="Code"
                value={formik.values.code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.code && Boolean(formik.errors.code)}
                helperText={formik.touched.code && formik.errors.code}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                select
                id="departmentType"
                name="departmentType"
                label="Type"
                value={formik.values.departmentType}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.departmentType && Boolean(formik.errors.departmentType)}
                helperText={formik.touched.departmentType && formik.errors.departmentType}
                {...DENSE_FIELD_PROPS}
                sx={{ minWidth: 120 }}
              >
                {visibleTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
          </Grid>
          <FormModalActions
            onCancel={() => {
              formik.resetForm();
              onClose(false);
            }}
            submitType="submit"
            submitDisabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          />
        </form>
      </Box>
    </BasicModal>
  );
};

export default DepartmentModal;
